import React, { useState, useEffect, useRef } from "react";
import { Mic2, Upload, Trash2, Loader2, Check, AlertTriangle, Play, ShieldCheck, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceStudio() {
  const { user } = useAuth();
  const { playSong } = usePlayer();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [lyrics, setLyrics] = useState("");
  const [title, setTitle] = useState("");
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const pollRef = useRef(null);

  const canVoice = user && user.plan_key !== "free" && ["active", "canceled"].includes(user.subscription_status);

  const load = () => api.get("/voice/profiles").then((r) => setProfiles(r.data.profiles)).catch(() => {});
  useEffect(() => { if (canVoice) load(); return () => pollRef.current && clearInterval(pollRef.current); }, [canVoice]);

  if (!canVoice) {
    return (
      <div className="px-6 pt-16 text-center max-w-md mx-auto">
        <div className="w-16 h-16 mx-auto rounded-2xl jx-cta grid place-items-center mb-4"><Mic2 className="text-white" size={28} /></div>
        <h1 className="text-2xl font-bold font-display">Voice Studio</h1>
        <p className="text-slate-400 mt-2">Record or upload your authorized voice and perform lyrics with AI. Available on Creator plans and above.</p>
        <button onClick={() => navigate("/plans")} className="jx-cta text-white font-semibold px-6 py-3 rounded-xl mt-5 inline-flex items-center gap-2"><Crown size={16} /> View Plans</button>
      </div>
    );
  }

  const upload = async () => {
    if (!file) return toast.error("Choose an audio file");
    if (!name.trim()) return toast.error("Name your voice profile");
    if (!consent) return toast.error("You must confirm authorization to use this voice");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("name", name); fd.append("consent", "true");
    try { await api.post("/voice/profiles", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Voice profile saved"); setName(""); setFile(null); setConsent(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
  };

  const del = async (id) => { await api.delete(`/voice/profiles/${id}`); load(); if (selected === id) setSelected(null); };

  const generate = async () => {
    if (!selected) return toast.error("Select a voice profile");
    if (!title.trim()) return toast.error("Add a title");
    if (!lyrics.trim()) return toast.error("Enter lyrics to perform");
    setJob({ status: "processing" }); setResult(null);
    try {
      const { data } = await api.post("/songs/generate", { title, mode: "voice", voice_profile_id: selected, lyrics, genre: "pop", performance: "singing" });
      pollRef.current = setInterval(async () => {
        const { data: s } = await api.get(`/generation/${data.job_id}`);
        setJob(s.job);
        if (s.job.status === "ready") { clearInterval(pollRef.current); setResult(s.song); }
        else if (s.job.status === "failed") { clearInterval(pollRef.current); setJob(null); toast.error(s.job.error || "Failed"); }
      }, 3000);
    } catch (e) { setJob(null); toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold font-display flex items-center gap-2 mb-1"><Mic2 className="text-purple-400" /> Voice Studio</h1>
      <p className="text-sm text-slate-400 mb-6">Perform lyrics with an authorized voice reference. Powered by fal.ai (minimax-music).</p>

      <div className="jx-card rounded-2xl p-5 mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Upload size={17} className="text-cyan-400" /> Add a voice profile</h2>
        <p className="text-xs text-slate-400 mb-3">Upload a clear recording of at least 15 seconds (MP3, WAV, M4A or OGG) containing singing or speech.</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Profile name (e.g. My Voice)" data-testid="voice-name" className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-3" />
        <input type="file" accept="audio/*" onChange={(e) => setFile(e.target.files[0])} data-testid="voice-file" className="w-full text-sm text-slate-400 mb-3 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-500/20 file:text-purple-300" />
        <label className="flex items-start gap-2 text-xs text-slate-300 mb-3">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="voice-consent" className="mt-0.5" />
          <span>I confirm this voice is mine or I have the necessary authorization to use it. I understand impersonating others is prohibited.</span>
        </label>
        <button onClick={upload} disabled={uploading} data-testid="voice-upload-btn" className="jx-cta text-white text-sm font-semibold px-5 py-2.5 rounded-xl disabled:opacity-50">{uploading ? "Uploading…" : "Save Voice Profile"}</button>
      </div>

      <h2 className="font-semibold mb-3">Your voice profiles</h2>
      <div className="space-y-2 mb-6">
        {profiles.length === 0 && <p className="text-sm text-slate-500">No voice profiles yet.</p>}
        {profiles.map((p) => (
          <div key={p.id} onClick={() => setSelected(p.id)} data-testid={`voice-profile-${p.id}`}
            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selected === p.id ? "border-purple-500 bg-purple-500/10" : "border-white/10"}`}>
            <ShieldCheck size={18} className="text-green-400" />
            <div className="flex-1"><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-slate-500">{Math.round(p.duration)}s • authorized</p></div>
            {selected === p.id && <Check size={16} className="text-purple-400" />}
            <button onClick={(e) => { e.stopPropagation(); del(p.id); }} className="text-slate-500 hover:text-rose-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      {selected && (
        <div className="jx-card rounded-2xl p-5">
          <h2 className="font-semibold mb-3">Perform with this voice</h2>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Song title" className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-3" />
          <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={5} placeholder="Lyrics to perform…" data-testid="voice-lyrics" className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-3 font-mono" />
          <button onClick={generate} disabled={job?.status === "processing"} data-testid="voice-generate-btn" className="jx-cta text-white font-semibold px-5 py-2.5 rounded-xl disabled:opacity-50 flex items-center gap-2">
            {job?.status === "processing" ? <><Loader2 className="animate-spin" size={16} /> Generating…</> : <><Mic2 size={16} /> Generate Performance</>}
          </button>
          {result && (
            <div className="mt-4 flex items-center gap-2 text-green-400"><Check size={16} /> Ready!
              <button onClick={() => playSong({ ...result, stream_url: `${api.defaults.baseURL}/stream/${result.id}`, creator_name: "You" })} className="ml-2 jx-cta text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1"><Play size={13} /> Play</button>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-600 mt-6 flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> Voice performances use a compatible AI music provider that performs lyrics guided by your reference audio. Results vary by provider capability. Report voice impersonation via any song's report option.</p>
    </div>
  );
}
